import { Component, type ReactNode } from "react";
import Markdown, { type Components, defaultUrlTransform } from "react-markdown";
import remarkGfm from "remark-gfm";

const safeLink: Components["a"] = ({ children, href }) =>
	href ? (
		<a href={href} target="_blank" rel="noreferrer noopener">
			{children}
		</a>
	) : (
		children
	);

const blockComponents: Components = {
	a: safeLink,
	table: ({ children }) => (
		// biome-ignore lint/a11y/noNoninteractiveTabindex: Keyboard users must be able to scroll wide tables.
		<div className="rich-text-table" tabIndex={0} role="region" aria-label="Table">
			<table>{children}</table>
		</div>
	),
	img: ({ alt }) => <span>{alt ?? ""}</span>,
};

const inlineComponents: Components = {
	a: ({ children }) => <>{children}</>,
	img: ({ alt }) => <>{alt ?? ""}</>,
	p: ({ children }) => <>{children}</>,
	h1: ({ children }) => <>{children}</>,
	h2: ({ children }) => <>{children}</>,
	h3: ({ children }) => <>{children}</>,
	h4: ({ children }) => <>{children}</>,
	h5: ({ children }) => <>{children}</>,
	h6: ({ children }) => <>{children}</>,
	blockquote: ({ children }) => <>{children}</>,
	ul: ({ children }) => <>{children}</>,
	ol: ({ children }) => <>{children}</>,
	li: ({ children }) => <>{children} </>,
	pre: ({ children }) => <>{children}</>,
};

export function markdownToPlainText(content: string) {
	return content
		.replace(/<[^>]*>/g, "")
		.replace(/!\[([^\]]*)\]\([^)]*\)/g, "$1")
		.replace(/\[([^\]]+)]\([^)]*\)/g, "$1")
		.replace(/^\s{0,3}#{1,6}\s+/gm, "")
		.replace(/^\s{0,3}>\s?/gm, "")
		.replace(/^\s{0,3}(?:[-+*]|\d+[.)])\s+/gm, "")
		.replace(/(`{1,3}|\*{1,3}|_{1,3}|~~)/g, "")
		.replace(/\\([\\`*{}[\]()#+\-.!_>])/g, "$1")
		.replace(/\n{3,}/g, "\n\n")
		.trim();
}

interface RichTextErrorBoundaryProps {
	children: ReactNode;
	fallback: ReactNode;
}

interface RichTextErrorBoundaryState {
	failed: boolean;
}

export class RichTextErrorBoundary extends Component<RichTextErrorBoundaryProps, RichTextErrorBoundaryState> {
	state: RichTextErrorBoundaryState = { failed: false };

	static getDerivedStateFromError(): RichTextErrorBoundaryState {
		return { failed: true };
	}

	render() {
		return this.state.failed ? this.props.fallback : this.props.children;
	}
}

export function RichText({
	content,
	inline = false,
	className,
}: {
	content: string;
	inline?: boolean;
	className?: string;
}) {
	const Wrapper = inline ? "span" : "div";
	const classes = [inline ? "rich-text-inline" : "rich-text-block", className].filter(Boolean).join(" ");
	const fallback = (
		<Wrapper className={classes} data-rich-text-fallback="plain-text">
			{markdownToPlainText(content)}
		</Wrapper>
	);

	return (
		<RichTextErrorBoundary key={`${inline ? "inline" : "block"}:${content}`} fallback={fallback}>
			<Wrapper className={classes}>
				<Markdown
					remarkPlugins={[remarkGfm]}
					components={inline ? inlineComponents : blockComponents}
					skipHtml
					urlTransform={defaultUrlTransform}
				>
					{content}
				</Markdown>
			</Wrapper>
		</RichTextErrorBoundary>
	);
}
